<script setup>
/**
 * The optional nudge: a small card docked over a page that still works.
 *
 * WHY THIS EXISTS. An optional set used to get the same treatment as a required
 * one — the middleware sent people to /prompt, so you clicked Clubs and got a
 * survey, with the page you actually wanted stashed in a query parameter. A
 * survey is not a wall, so it does not get a wall's affordances.
 *
 * REQUIRED SETS ARE NOT DRAWN HERE, deliberately. Their blocking is the ROUTE:
 * userFormPrompt.global.js redirects on every navigation, so there is no state
 * in which somebody is past it. Moving that into a mounted component would make
 * the guarantee depend on the component mounting — a render error or a slow
 * hydrate and the member is through a wall that is not there.
 *
 * Mounted once in app.vue for signed-in members.
 */
import {computed, onMounted, ref, watch} from 'vue';
import {useRoute, useRouter} from 'vue-router';
import PromptIntroCard from '~/components/prompts/PromptIntroCard.vue';
import {useAuthService} from '~/composables/useAuthService';
import {useHttpRequest} from '~/composables/useHttpRequest';
import {endpoints} from '~/network/endpoints.js';
import {competitionQuestionsOpen} from '~/composables/useInterruptions';
import {promptDockCard, promptSnoozeUntil, shouldShowPromptDock} from '~/composables/useUserFormPromptCard';

const route = useRoute();
const router = useRouter();
const {isAuthenticated, user, updateLocalUser} = useAuthService();
const {httpRequest} = useHttpRequest();

const busy = ref(false);
const hiddenThisView = ref(false);

const pending = computed(() => user.value?.pendingFormPrompt ?? null);
const card = computed(() => promptDockCard(pending.value));

// A competition question dialog on screen is interruption enough.

const show = computed(() => shouldShowPromptDock({
  authenticated: isAuthenticated.value,
  pending: pending.value,
  path: route.path,
  hidden: hiddenThisView.value || competitionQuestionsOpen.value,
}));

// A new set gets a fresh look-in on the NEXT view, not this one.
//
// Watching the pending id and clearing `hidden` immediately meant declining one
// set produced the next set's card in the same breath: you pressed "Not now"
// and were asked again a quarter of a second later, which is precisely the
// nagging the card exists to avoid. Anything declined here stays away until the
// member navigates.
watch(() => pending.value?.id, (next, previous) => {
  if (previous == null) hiddenThisView.value = false;
});

// A navigation is the moment a nudge may reappear. One card per page view, so
// a member who has said "not now" is left alone until they move on.
watch(() => route.path, () => {
  hiddenThisView.value = false;
});

const decline = async (endpoint, body) => {
  if (busy.value) return;
  busy.value = true;
  try {
    await httpRequest(endpoint, body);
    // The server decides what a decline MEANT — the third one is a dismissal,
    // not a snooze — so the answer comes back from a refreshed session rather
    // than being guessed here.
    await updateLocalUser();
  } catch {
    // A nudge that cannot be put away is worse than one that reappears next
    // visit, so it goes either way and the server is asked again later.
  } finally {
    hiddenThisView.value = true;
    busy.value = false;
  }
};

const later = () => decline(endpoints.userFormPrompts.snooze(pending.value.id), {
  until: promptSnoozeUntil(),
});

const never = () => decline(endpoints.userFormPrompts.dismiss(pending.value.id), {});

/** Yes. The form itself lives at /prompt, which knows how to run a set. */
const open = () => {
  hiddenThisView.value = true;
  router.push('/prompt?started=1&redirectUrl=' + encodeURIComponent(route.fullPath));
};

onMounted(() => {
  hiddenThisView.value = false;
});
</script>

<template>
  <prompt-intro-card
      v-if="show"
      :card="card"
      placement="docked"
      :busy="busy"
      @continue="open"
      @later="later"
      @never="never"/>
</template>
